import { ChevronLeft, ChevronRight } from 'lucide-react'

import { ActionInfoQuiet, Button, ErrorNotice, QueryStatePanel, ScreenInfo, StatusMessage } from '@krp/ui-kit'

import { CashierSearch } from './CashierSearch'
import { Legend } from './Legend'
import { ErrorBlock, SrOnly } from './parts'
import { ScheduleGrid } from './ScheduleGrid'
import type { CashiersActions, CashiersVm } from './screenTypes'
import './schedule.css'

const SCREEN_INFO = [
  'Сверху выберите кассира: поиск по имени и подсказки — последние выбранные и первые по алфавиту. Пока вы никого не выбрали, предложен кассир открытой смены.',
  'Ниже его месяц: в строках все активные объекты, в столбцах дни — число и день недели, справа «Всего» — сколько дней кассир выходит («Р») на этом объекте. Сегодняшний день в шапке синий. При нехватке места прокручиваются только дни.',
  'Левый клик по клетке ставит кассира на объект в этот день («Р», клетка залита цветом кассира); ещё раз — очищает. Правый клик (на телефоне — долгое нажатие, с клавиатуры — клавиша меню или Shift+F10) открывает меню: «Р — Выходит», «В — Выходной день» (холодный тон со штриховкой), «Н — Не выходит» (серая клетка) и «Очистить». Серая клетка с приглушённой «Н» — день не отмечен. В «Всего» считаются только дни «Р». Сохранение идёт сразу, кнопки «Сохранить» нет; пока ответа нет, в клетке крутится загрузчик.',
  'Если кассир в этот день уже выходит («Р») на другом объекте, не отмеченная клетка выглядит как «Н»: при наведении в ней появляется замок, а в подсказке написано, на каком объекте он занят. Поставить «Р» сюда нельзя; «В» и «Н» ставятся всегда.',
  'Правка графика — у того, кому выдано право его править; остальные только смотрят.',
] as const

function GridSkeleton() {
  return (
    <div className="sch-skeleton" role="status" aria-live="polite">
      <SrOnly>Загружаем график по кассирам…</SrOnly>
      {Array.from({ length: 4 }, (_, row) => (
        <div className="sch-skeleton__row" key={row} aria-hidden="true">
          {Array.from({ length: 7 }, (_, cell) => (
            <span className="sch-skeleton__cell" key={cell} />
          ))}
        </div>
      ))}
    </div>
  )
}

/**
 * Экран «По кассирам»: выбор кассира сверху и его месяц ниже — объекты строками, дни столбцами, «Всего» справа. Левый клик и меню
 * статусов клетки работают так же, как в таблице объекта. Экран показывает ВСЕ активные объекты. Экран ничего не
 * запрашивает сам: vm и действия — от binding; месяц приходит из адреса окна. Группы сверху вниз: заголовок, переключатель разделов,
 * навигация по месяцам, строка «Кассир», легенда, таблица.
 */
export function CashiersScreen({ vm, actions }: { vm: CashiersVm; actions: CashiersActions }) {
  const loading = vm.load.status === 'loading' || vm.load.status === 'idle'
  return (
    <section aria-labelledby="schedule-cashiers-title" className="sch">
      <header className="sch-head">
        <div className="sch-head__title">
          <h2 id="schedule-cashiers-title">График по кассирам</h2>
          <ScreenInfo title="График по кассирам" lines={SCREEN_INFO} />
        </div>
      </header>

      <nav className="sch-switch" aria-label="Разделы графика выхода">
        <ActionInfoQuiet>
          <Button variant="secondary" actionId="schedule.cashiers.show-places" onClick={actions.showPlaces}>
            Объекты
          </Button>
        </ActionInfoQuiet>
        <span className="sch-switch__current" aria-current="page">
          По кассирам
        </span>
      </nav>

      <div className="sch-toolbar" role="toolbar" aria-label="Месяц графика">
        <div className="sch-toolbar__nav">
          <ActionInfoQuiet>
            <Button variant="secondary" icon={<ChevronLeft size={16} aria-hidden="true" />} aria-label="Предыдущий месяц" actionId="schedule.cashiers.previous-month" onClick={actions.previous} />
            <strong className="sch-period" aria-live="polite">
              {vm.title}
            </strong>
            <Button variant="secondary" icon={<ChevronRight size={16} aria-hidden="true" />} aria-label="Следующий месяц" actionId="schedule.cashiers.next-month" onClick={actions.next} />
            <Button variant="secondary" actionId="schedule.cashiers.today" onClick={actions.today}>
              Текущий месяц
            </Button>
          </ActionInfoQuiet>
          {vm.refreshing && (
            <span className="sch-refreshing" role="status">
              Обновляется…
            </span>
          )}
        </div>
      </div>

      <div className="sch-pick" role="group" aria-label="Кассир">
        <span className="sch-select__label">Кассир</span>
        <CashierSearch
          label="Выбрать кассира"
          current={vm.chosenId !== null && vm.chosenName !== null ? { id: vm.chosenId, name: vm.chosenName } : null}
          title={vm.fromShift ? 'Выбран кассир открытой смены' : undefined}
          people={vm.people}
          excluded={vm.chosenId === null ? [] : [vm.chosenId]}
          recent={vm.recent}
          onPick={actions.choose}
        />
      </div>

      <Legend />

      {vm.directoryError !== null && <ErrorNotice error={vm.directoryError} title="Имена кассиров не получены: кассиры подписаны по номеру" />}
      {vm.error !== null && <ErrorBlock error={vm.error} />}

      {loading ? (
        <GridSkeleton />
      ) : vm.load.status !== 'ready' ? (
        <QueryStatePanel
          query={vm.load}
          emptyText=""
          onRetry={actions.reload}
          retryActionId="schedule.cashiers.reload"
          titles={{ forbidden: 'Нет доступа к графику выхода', unavailable: 'Служба графика недоступна', error: 'Не удалось получить график по кассирам' }}
        />
      ) : vm.chosenId === null ? (
        <StatusMessage kind="empty" title="Выберите кассира" text="Начните вводить имя в поле «Выбрать кассира» или выберите из подсказок: появится его месяц по всем объектам." />
      ) : vm.rows.length === 0 ? (
        <StatusMessage kind="empty" title="Объектов пока нет" text="Объекты создаются на первом экране раздела «График выхода»." />
      ) : (
        <ScheduleGrid
          caption={`Объекты по дням: ${vm.chosenName ?? ''}, ${vm.title}`}
          cornerLabel="Объект"
          days={vm.days}
          rows={vm.rows}
          editable={vm.editable}
          refreshing={vm.refreshing}
          scrollKey={`${vm.chosenId ?? ''}|${vm.title}`}
          denied={vm.denied}
          onToggle={actions.toggle}
          onSetKind={actions.setKind}
          onDismissDenied={actions.dismissDenied}
        />
      )}
    </section>
  )
}
